import { Alert, Box, Chip, CircularProgress, Divider, List, ListSubheader, Paper, Stack, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDailyItemsByDateQuery } from '../../../../hooks/queries/useActivitiesQuery';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { getTruckPriceTypeLabel } from '../../../../utils/enums';
import LicensePlate from '../../../LicensePlate';
import Icon from '../../../shared/Icon';
import ReportItemCard from '../../../shared/ReportItemCard';
import ModalBody, { ModalProps } from '../../ModalBody';

interface ControlValidationItemsModalProps extends ModalProps {
    date: string;
}

const ControlValidationItemsModal: React.FC<ControlValidationItemsModalProps> = ({ open, handleClose, date }) => {
    const { t } = useTranslation();

    const { data, isLoading, error } = useDailyItemsByDateQuery({ date });

    const totals = useMemo(() => {
        if (!data) return { users: 0, machines: 0, tools: 0, trucks: 0 };
        return {
            users: data.users.length,
            machines: data.machines.length,
            tools: data.tools.length,
            trucks: data.trucks.length,
        };
    }, [data]);

    return (
        <ModalBody
            title={capitalizeFirstLetter(t('controlOfValidationItems'))}
            open={open}
            handleClose={handleClose}
            loading={isLoading}
            width={700}
            isOkVisible={false}
        >
            {isLoading && (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                    <CircularProgress />
                </Box>
            )}

            {data && (
                <Stack spacing={2}>
                    <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(150px, 1fr))" gap={2}>
                        <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                            <Icon name="person" color="primary" sx={{ fontSize: 40 }} />

                            <Typography variant="h4">{totals.users}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {capitalizeFirstLetter(t('users'))}
                            </Typography>
                        </Paper>
                        <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                            <Icon name="precisionManufacturing" color="secondary" sx={{ fontSize: 40 }} />
                            <Typography variant="h4">{totals.machines}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {capitalizeFirstLetter(t('machines'))}
                            </Typography>
                        </Paper>
                        <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                            <Icon name="construction" color="success" sx={{ fontSize: 40 }} />
                            <Typography variant="h4">{totals.tools}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {capitalizeFirstLetter(t('tools'))}
                            </Typography>
                        </Paper>
                        <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                            <Icon name="localShipping" color="warning" sx={{ fontSize: 40 }} />
                            <Typography variant="h4">{totals.trucks}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {capitalizeFirstLetter(t('trucks'))}
                            </Typography>
                        </Paper>
                    </Box>

                    <Divider />

                    {data.users && data.users.length > 0 && (
                        <List
                            sx={{ gap: 1, display: 'flex', flexDirection: 'column' }}
                            subheader={
                                <ListSubheader sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Icon name="person" />
                                    {capitalizeFirstLetter(t('users'))} ({data.users.length})
                                </ListSubheader>
                            }
                        >
                            {data.users
                                .sort((a, b) =>
                                    formatName(a.user.firstName, a.user.lastName).localeCompare(formatName(b.user.firstName, b.user.lastName))
                                )
                                .map((userItem) => {
                                    const totalQuantity = userItem.sites.reduce((acc, siteQty) => acc + siteQty.quantity, 0);
                                    return (
                                        <ReportItemCard
                                            key={userItem.user._id}
                                            title={formatName(userItem.user.firstName, userItem.user.lastName)}
                                            hidePrice
                                            extraInfo={
                                                <Typography
                                                    color={totalQuantity === 1 ? 'textPrimary' : 'error'}
                                                >{`${totalQuantity} ${t('day')}`}</Typography>
                                            }
                                            hideMajorAction
                                            disabled
                                        >
                                            <Box display="flex" flexWrap="wrap" gap={1}>
                                                {userItem.sites.map((siteQty) => (
                                                    <Chip
                                                        key={siteQty.site._id}
                                                        label={`${siteQty.site.name}: ${siteQty.quantity} ${t('day')}`}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                ))}
                                            </Box>
                                        </ReportItemCard>
                                    );
                                })}
                        </List>
                    )}

                    {data.machines && data.machines.length > 0 && (
                        <List
                            sx={{ gap: 1, display: 'flex', flexDirection: 'column' }}
                            subheader={
                                <ListSubheader sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Icon name="precisionManufacturing" />
                                    {capitalizeFirstLetter(t('machines'))} ({data.machines.length})
                                </ListSubheader>
                            }
                        >
                            {data.machines
                                .sort((a, b) => {
                                    const typeA = a.machine.machineTypeId.displayOrder || Infinity;
                                    const typeB = b.machine.machineTypeId.displayOrder || Infinity;
                                    return typeA - typeB || formatName(a.machine.designation).localeCompare(formatName(b.machine.designation));
                                })
                                .map((machineItem) => {
                                    const totalQuantity = machineItem.sites.reduce((acc, siteQty) => acc + siteQty.quantity, 0);
                                    return (
                                        <ReportItemCard
                                            key={machineItem.machine._id}
                                            title={machineItem.machine.designation}
                                            hidePrice
                                            extraInfo={
                                                <Typography
                                                    color={totalQuantity === 1 ? 'textPrimary' : 'error'}
                                                >{`${totalQuantity} ${t('day')}`}</Typography>
                                            }
                                            hideMajorAction
                                            disabled
                                        >
                                            <Box display="flex" flexWrap="wrap" gap={1}>
                                                {machineItem.sites.map((siteQty) => (
                                                    <Chip
                                                        key={siteQty.site._id}
                                                        label={`${siteQty.site.name}: ${siteQty.quantity} ${t('day')}`}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                ))}
                                            </Box>
                                        </ReportItemCard>
                                    );
                                })}
                        </List>
                    )}

                    {data.tools && data.tools.length > 0 && (
                        <List
                            sx={{ gap: 1, display: 'flex', flexDirection: 'column' }}
                            subheader={
                                <ListSubheader sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Icon name="construction" />
                                    {capitalizeFirstLetter(t('tools'))} ({data.tools.length})
                                </ListSubheader>
                            }
                        >
                            {data.tools
                                .sort((a, b) => {
                                    const typeA = a.tool.toolTypeId.displayOrder || Infinity;
                                    const typeB = b.tool.toolTypeId.displayOrder || Infinity;
                                    return typeA - typeB || formatName(a.tool.designation).localeCompare(formatName(b.tool.designation));
                                })
                                .map((toolItem) => {
                                    const totalQuantity = toolItem.sites.reduce((acc, siteQty) => acc + siteQty.quantity, 0);
                                    return (
                                        <ReportItemCard
                                            key={toolItem.tool._id}
                                            title={toolItem.tool.designation}
                                            hidePrice
                                            extraInfo={
                                                <Typography
                                                    color={totalQuantity === 1 ? 'textPrimary' : 'error'}
                                                >{`${totalQuantity} ${t('day')}`}</Typography>
                                            }
                                            hideMajorAction
                                            disabled
                                        >
                                            <Box display="flex" flexWrap="wrap" gap={1}>
                                                {toolItem.sites.map((siteQty) => (
                                                    <Chip
                                                        key={siteQty.site._id}
                                                        label={`${siteQty.site.name}: ${siteQty.quantity} ${t('day')}`}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                ))}
                                            </Box>
                                        </ReportItemCard>
                                    );
                                })}
                        </List>
                    )}

                    {data.trucks && data.trucks.length > 0 && (
                        <List
                            sx={{ gap: 1, display: 'flex', flexDirection: 'column' }}
                            subheader={
                                <ListSubheader sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Icon name="localShipping" />
                                    {capitalizeFirstLetter(t('trucks'))} ({data.trucks.length})
                                </ListSubheader>
                            }
                        >
                            {data.trucks
                                .sort((a, b) => {
                                    const typeA = a.truck.truckTypeId.displayOrder || Infinity;
                                    const typeB = b.truck.truckTypeId.displayOrder || Infinity;
                                    return typeA - typeB || formatName(a.truck.designation).localeCompare(formatName(b.truck.designation));
                                })
                                .map((truckItem) => {
                                    const totalQuantity = truckItem.sites.reduce((acc, siteQty) => acc + siteQty.quantity, 0);
                                    return (
                                        <ReportItemCard
                                            key={truckItem.truck._id}
                                            titleComponent={<LicensePlate number={truckItem.truck.plateNumber} />}
                                            title={`${truckItem.truck.truckTypeId.designation} ${truckItem.truck.userId?.username || ''}`}
                                            hidePrice
                                            extraInfo={
                                                <Typography
                                                    color={totalQuantity === 1 ? 'textPrimary' : 'error'}
                                                >{`${totalQuantity} ${t(getTruckPriceTypeLabel(truckItem.priceType))}`}</Typography>
                                            }
                                            hideMajorAction
                                            disabled
                                        >
                                            <Box display="flex" flexWrap="wrap" gap={1}>
                                                {truckItem.sites.map((siteQty) => (
                                                    <Chip
                                                        key={siteQty.site._id}
                                                        label={`${siteQty.site.name}: ${siteQty.quantity} ${t(getTruckPriceTypeLabel(truckItem.priceType))}`}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                ))}
                                            </Box>
                                        </ReportItemCard>
                                    );
                                })}
                        </List>
                    )}

                    {data.users.length === 0 && data.machines.length === 0 && data.tools.length === 0 && data.trucks.length === 0 && (
                        <Alert severity="info">{capitalizeFirstLetter(t('noDataAvailableForThisDate'))}</Alert>
                    )}
                </Stack>
            )}
        </ModalBody>
    );
};

export default ControlValidationItemsModal;
